import { formatToUTC } from "lib/utils/datetime";
import { Query, QueryGroup, QueryRule } from "./types";
import {
  isDateTimeRange,
  isDateTimeStrings,
  isDistance,
  isNumericRange,
  validateDateTimeValues,
} from "./utils";

type MongoImplicitAnd = Record<string, unknown>;

interface MongoAnd {
  $and: Array<MongoRule>;
}

interface MongoOr {
  $or: Array<MongoRule>;
}

type MongoRule = MongoImplicitAnd | MongoAnd | MongoOr;

function isOr(rule: MongoRule): rule is MongoOr {
  return (rule as MongoOr).$or !== undefined;
}

function isAnd(rule: MongoRule): rule is MongoAnd {
  return (rule as MongoAnd).$and !== undefined;
}

const groupHasNoSubGroupsOrJustOneOR = (group: QueryGroup) => {
  let foundOr = false;
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i !== group.rules.length; ++i) {
    const rule = group.rules[i];
    if ("combinator" in rule) {
      if (!foundOr && rule.combinator === "or") {
        foundOr = true;
      } else {
        return false;
      }
    }
  }

  return true;
};

const ruleToMongo = (
  rule: QueryRule,
  timezone: string
): MongoImplicitAnd | null => {
  if (rule.attribute == null) {
    return null;
  }

  if (rule.value != null && isDistance(rule.value)) {
    return null;
  }

  if (
    (rule.value == null ||
      (typeof rule.value === "string" && rule.value.trim() === "")) &&
    rule.operator !== "Empty" &&
    rule.operator !== "is_not_empty"
  ) {
    throw new Error("Missing value");
  }

  const stringValue = rule.value?.toString().trim() ?? "";

  let value;
  switch (rule.operator) {
    case "equalsIgnoreCase":
      value = { $regex: stringValue, $options: "i" };
      break;
    case "contains":
      value = { $regex: `.*${stringValue}.*` };
      break;
    case "StartsWith":
      value = { $regex: `${stringValue}.*` };
      break;
    case "EndsWith":
      value = { $regex: `.*${stringValue}` };
      break;
    case "IsNot":
      value = { $ne: stringValue };
      break;
    case "Empty":
      // TODO what about null?
      value = "";
      break;
    case "is_not_empty":
      value = { $exists: true, $ne: "" };
      break;

    case "equals":
      if (
        rule.value != null &&
        isDateTimeStrings(rule.value) &&
        rule.value.date != null &&
        rule.value.time != null &&
        validateDateTimeValues(rule.value.date, rule.value.time)
      ) {
        value = formatToUTC(`${rule.value.date} ${rule.value.time}`, timezone);
      } else if (rule.value != null && typeof rule.value === "boolean") {
        value = rule.value;
      } else if (rule.value != null && Number.isFinite(Number(rule.value))) {
        value = Number(rule.value);
      } else if (rule.value != null && typeof rule.value === "string") {
        value = stringValue;
      } else {
        throw new Error("Invalid value");
      }
      break;
    case "notEqual":
      if (
        rule.value != null &&
        isDateTimeStrings(rule.value) &&
        rule.value.date != null &&
        rule.value.time != null &&
        validateDateTimeValues(rule.value.date, rule.value.time)
      ) {
        value = {
          $ne: formatToUTC(`${rule.value.date} ${rule.value.time}`, timezone),
        };
      } else if (rule.value != null && Number.isFinite(Number(rule.value))) {
        value = { $ne: Number(rule.value) };
      } else {
        throw new Error("Invalid value");
      }
      break;
    case "greaterThan":
      if (
        rule.value != null &&
        isDateTimeStrings(rule.value) &&
        rule.value.date != null &&
        rule.value.time != null &&
        validateDateTimeValues(rule.value.date, rule.value.time)
      ) {
        value = {
          $gt: formatToUTC(`${rule.value.date} ${rule.value.time}`, timezone),
        };
      } else if (rule.value != null && Number.isFinite(Number(rule.value))) {
        value = { $gt: Number(rule.value) };
      } else {
        throw new Error("Invalid value");
      }
      break;
    case "greaterThanEq":
      if (
        rule.value != null &&
        isDateTimeStrings(rule.value) &&
        rule.value.date != null &&
        rule.value.time != null &&
        validateDateTimeValues(rule.value.date, rule.value.time)
      ) {
        value = {
          $gte: formatToUTC(`${rule.value.date} ${rule.value.time}`, timezone),
        };
      } else if (rule.value != null && Number.isFinite(Number(rule.value))) {
        value = { $gte: Number(rule.value) };
      } else {
        throw new Error("Invalid value");
      }
      break;
    case "lessThan":
      if (
        rule.value != null &&
        isDateTimeStrings(rule.value) &&
        rule.value.date != null &&
        rule.value.time != null &&
        validateDateTimeValues(rule.value.date, rule.value.time)
      ) {
        value = {
          $lt: formatToUTC(`${rule.value.date} ${rule.value.time}`, timezone),
        };
      } else if (rule.value != null && Number.isFinite(Number(rule.value))) {
        value = { $lt: Number(rule.value) };
      } else {
        throw new Error("Invalid value");
      }
      break;
    case "lessThanEq":
      if (
        rule.value != null &&
        isDateTimeStrings(rule.value) &&
        rule.value.date != null &&
        rule.value.time != null &&
        validateDateTimeValues(rule.value.date, rule.value.time)
      ) {
        value = {
          $lte: formatToUTC(`${rule.value.date} ${rule.value.time}`, timezone),
        };
      } else if (rule.value != null && Number.isFinite(Number(rule.value))) {
        value = { $lte: Number(rule.value) };
      } else {
        throw new Error("Invalid value");
      }
      break;
    case "range":
      if (
        rule.value != null &&
        isDateTimeRange(rule.value) &&
        rule.value.start?.date != null &&
        rule.value.start?.time != null &&
        rule.value.end?.date != null &&
        rule.value.end?.time != null &&
        validateDateTimeValues(
          rule.value.start.date,
          rule.value.start.time,
          rule.value.end.date,
          rule.value.end.time
        )
      ) {
        value = {
          $gte: formatToUTC(
            `${rule.value.start.date} ${rule.value.start.time}`,
            timezone
          ),
          $lte: formatToUTC(
            `${rule.value.end.date} ${rule.value.end.time}`,
            timezone
          ),
        };
      } else if (
        rule.value != null &&
        isNumericRange(rule.value) &&
        Number.isFinite(Number(rule.value.from)) &&
        Number.isFinite(Number(rule.value.to)) &&
        rule.value.from < rule.value.to
      ) {
        value = { $gte: Number(rule.value.from), $lte: Number(rule.value.to) };
      } else {
        throw new Error("Invalid value");
      }
      break;
    default:
      value = rule.value;
  }

  return { [rule.attribute]: value };
};

const groupToMongo = (group: QueryGroup, timezone: string): MongoRule => {
  if (group.combinator == null) {
    throw new Error("missing combinator");
  }

  if (group.combinator === "and") {
    // check for repeated attributes
    const bag: Record<string, boolean> = {};
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < group.rules.length; ++i) {
      const rule = group.rules[i];
      if ("attribute" in rule && rule.attribute != null) {
        if (bag[rule.attribute]) {
          throw new Error("Duplicate keys in AND");
        }

        bag[rule.attribute] = true;
      }
    }
  } else if (group.combinator === "or") {
    // check for distinct attributes
    let key;
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < group.rules.length; ++i) {
      const rule = group.rules[i];
      if ("attribute" in rule && rule.attribute != null) {
        if (key != null && key !== rule.attribute) {
          throw new Error("Distinct keys in OR");
        }

        key = rule.attribute;
      }
    }
  }

  // we can't use an implicit AND if:
  //  - we're in a OR
  //  - we have nested ANDs (unless only one and no other rules)
  //  - we have more than one nested OR
  const implicitAnd =
    group.combinator === "and" &&
    (group.rules.length <= 1 || groupHasNoSubGroupsOrJustOneOR(group));

  const initialValue = implicitAnd ? {} : { [`$${group.combinator}`]: [] };

  const mongo = group.rules.reduce<MongoRule>((mongoRule, queryRule) => {
    let rule;

    if ("attribute" in queryRule && queryRule.attribute != null) {
      rule = ruleToMongo(queryRule, timezone);
    } else if ("combinator" in queryRule && queryRule.combinator != null) {
      rule = groupToMongo(queryRule, timezone);
    }

    if (rule != null) {
      if (implicitAnd) {
        Object.assign(mongoRule, rule);
      } else if (isAnd(mongoRule)) {
        mongoRule.$and.push(rule);
      } else if (isOr(mongoRule)) {
        mongoRule.$or.push(rule);
      }
    }

    return mongoRule;
  }, initialValue);

  return mongo;
};

export const queryToMongo = (query: Query, timezone: string): MongoRule => {
  return groupToMongo(query, timezone);
};
